import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { Checkbox, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material'
import { apiGet, apiPost } from '../../generic/Api_Functions'
import SmartSelect from '../../generic/smartSelect/SmartSelect'
import AlertUI from '../../generic/AlertUI'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { allowsPartialQuantity, allowsDecimalQuantity, normalizeInstanceQuantity } from '../../Mos/Widgets/MachineMaterial/instanceGranularity'

// Sun binds page/size/allSelected as primitives, so a request without a RequestPagination is
// rejected (400) before the controller runs. A reference's in-machine instances is a small set, so a
// single large page is requested (the endpoint's in-memory pagination is built for small sets too).
const ALL_INSTANCES_REQUEST = { page: 0, size: 1000, allSelected: false }

// Unload tab of MachineStorageDialog — returns material already loaded in a Machine Storage back to
// the general stock (CU7, RAW MATERIAL variant, authority-gated). Content only: the enclosing dialog
// owns the modal shell and its accept button triggers `submit` (exposed via ref). Reverse of the
// load tab: pick a Machine Storage, list the reference's instances loaded there, choose how much of
// each to return. Granularity from the reference flags (per row):
//   - whole-only (case 1): checkboxes -> instanceSelections: [{ instanceId }]
//   - partial (cases 2/3/4): a quantity per instance (integer for case 2, decimal for 3/4) capped at
//     quantityAvailable -> instanceSelections: [{ instanceId, quantity }]
const WithdrawMachineStoragePanel = forwardRef(({ open = false, reference = {}, onClose = () => {}, onSuccess = () => {}, onLoadingChange = () => {} }, ref) => {
  const { t_ } = useSmartTranslation()
  const [AlertElement, showAlert, dismiss] = AlertUI()
  const [machineStorages, setMachineStorages] = useState([])
  const [selectedMachineStorage, setSelectedMachineStorage] = useState({})
  const [instances, setInstances] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const [quantityByInstanceId, setQuantityByInstanceId] = useState({})
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) return
    setSelectedMachineStorage({})
    setInstances([])
    setSelectedIds([])
    setQuantityByInstanceId({})
    setLoading(false)
    dismiss()
    apiGet('machineStorages').then((data) => setMachineStorages(data || []))
  }, [open, dismiss])

  useEffect(() => { onLoadingChange(loading) }, [loading, onLoadingChange])

  // Load the reference's instances currently in the chosen Machine Storage whenever it changes, and
  // clear any in-progress selection so it never carries over to a different storage.
  const machineStorageId = selectedMachineStorage.machineStorageId
  useEffect(() => {
    if (!open || !machineStorageId || !reference.id) {
      setInstances([])
      return
    }
    setSelectedIds([])
    setQuantityByInstanceId({})
    apiGet('rawMaterials/machineStorage/' + machineStorageId + '/reference/' + reference.id + '/instances', ALL_INSTANCES_REQUEST)
      .then((result) => setInstances(Array.isArray(result) ? result : (result?.content ?? [])))
  }, [open, machineStorageId, reference.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Granularity is a property of the reference, so every row shares the first row's flags.
  const flags = instances.length > 0 ? { fractionable: instances[0].fractionable, unitary: instances[0].unitary } : {}
  const partial = allowsPartialQuantity(flags)
  const allowDecimal = allowsDecimalQuantity(flags)

  const availableById = useMemo(
    () => instances.reduce((acc, row) => { acc[row.id] = row.quantityAvailable; return acc }, {}),
    [instances]
  )

  const toggleInstance = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((current) => current !== id) : [...prev, id]))

  const setQuantity = (id, value) =>
    setQuantityByInstanceId((prev) => ({ ...prev, [id]: value }))

  const selectedCount = partial
    ? Object.values(quantityByInstanceId).filter((value) => Number(value) > 0).length
    : selectedIds.length

  const buildInstanceSelections = () => {
    if (partial) {
      return Object.entries(quantityByInstanceId)
        .map(([instanceId, value]) => ({
          instanceId: Number(instanceId),
          quantity: normalizeInstanceQuantity(value, { decimal: allowDecimal, available: availableById[instanceId] })
        }))
        .filter((item) => item.quantity > 0)
    }
    return selectedIds.map((instanceId) => ({ instanceId }))
  }

  const submitReturn = async () => {
    if (!machineStorageId) return
    const instanceSelections = buildInstanceSelections()
    if (instanceSelections.length === 0) {
      showAlert({ severity: 'warning', title: t_('Select at least one instance to return') })
      return
    }
    setLoading(true)
    try {
      await apiPost('rawMaterials/machineStorage/' + machineStorageId + '/return', { instanceSelections })
      onSuccess()
      onClose()
    } catch (error) {
      showAlert({ severity: 'error', title: t_(error.message) })
    } finally {
      setLoading(false)
    }
  }

  useImperativeHandle(ref, () => ({ submit: submitReturn }))

  return (
    <Stack spacing={2} sx={{ minWidth: '24rem' }}>
      {AlertElement}
      <Typography variant='body2' color='text.secondary'>
        {t_('Reference') + ': ' + (reference.code || '')}
      </Typography>
      <SmartSelect
        selectableOptions={machineStorages}
        value={selectedMachineStorage.machineStorageId ? selectedMachineStorage : null}
        onChange={(newValue) => setSelectedMachineStorage(newValue || {})}
        optionLabelTransform={(option) => option?.machineName || ''}
        label={t_('Machine storage')}
      />
      {machineStorageId && instances.length === 0 && (
        <Typography variant='body2' color='text.secondary'>
          {t_('No instances of this reference are loaded in the selected machine storage')}
        </Typography>
      )}
      {instances.length > 0 && (
        <Table size='small'>
          <TableHead>
            <TableRow>
              {!partial && <TableCell padding='checkbox' />}
              <TableCell>{t_('Code')}</TableCell>
              <TableCell>{t_('Batch')}</TableCell>
              <TableCell align='right'>{t_('Available in machine')}</TableCell>
              {partial && <TableCell align='right'>{t_('Quantity to return')}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {instances.map((row) => (
              <TableRow key={row.id} hover>
                {!partial && (
                  <TableCell padding='checkbox'>
                    <Checkbox
                      size='small'
                      checked={selectedIds.includes(row.id)}
                      onChange={() => toggleInstance(row.id)}
                      inputProps={{ 'aria-label': t_('Select instance') }}
                    />
                  </TableCell>
                )}
                <TableCell>{row.code}</TableCell>
                <TableCell>{row.batch}</TableCell>
                <TableCell align='right'>{row.quantityAvailable}</TableCell>
                {partial && (
                  <TableCell align='right'>
                    <TextField
                      type='number'
                      size='small'
                      value={quantityByInstanceId[row.id] ?? ''}
                      onChange={(event) => setQuantity(row.id, event.target.value)}
                      inputProps={{ min: 0, max: row.quantityAvailable, step: allowDecimal ? 'any' : 1 }}
                    />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {selectedCount > 0 && (
        <Typography variant='body2' color='text.secondary'>{selectedCount + ' ' + t_('items selected')}</Typography>
      )}
    </Stack>
  )
})

export default WithdrawMachineStoragePanel
